import { useState } from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { ChipButton } from '@apigrowfinance/pbolt-chip-button';
import { Restricted } from '@apigrowfinance/pbolt-permission-provider';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import LoadingError from '@components/loadingError/LoadingError';
import CreditLimitPanelContainer from '@components/overviewPanels/creditLimitPanel/CreditLimitPanelContainer';
import InstallmentCreditLimitPieContainer from '@components/overviewPanels/installmentCreditLimitPiePanel/InstallmentCreditLimitPieContainer';
import MonthlyCreditLimitPieContainer from '@components/overviewPanels/monthlyCreditLimitPiePanel/MonthlyCreditLimitPieContainer';
import OverviewItem, {
    panelItem,
} from '@components/overviewPanels/OverviewItem';
import OverviewLimitContainer from '@components/overviewPanels/overviewLimitPanel/OverviewLimitContainer';
import WithdrawalLimitPieContainer from '@components/overviewPanels/withdrawalLimitPiePanel/WithdrawalLimitPieContainer';
import useGetLimits from '@hooks/queries/useGetLimits';
import EditLimitsDialog from '@pages/limits/EditLimitsDialog';
import getEnumTranslation from '@utils/getEnumTranslation';

const Limits = () => {
    const { accountId } = useParams();
    const { theme } = useTheme();
    const [open, setOpen] = useState(false);

    const {
        data: limits,
        isLoading: limitsLoading,
        isError,
        refetch,
    } = useGetLimits(+accountId);

    const handleClick = () => {
        setOpen(true);
    };

    if (isError) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    width: '100%',
                    height: 'calc(100vh - 88px)',
                }}>
                <LoadingError />
            </Box>
        );
    }

    const panelsToDisplay: panelItem[] = [
        {
            component: <CreditLimitPanelContainer clickableVariant={false} />,
            grid: { xs: 12, md: 6 },
        },
        {
            component: <OverviewLimitContainer />,
            grid: { xs: 12, md: 6 },
        },
        {
            component: <WithdrawalLimitPieContainer />,
            grid: { xs: 12, md: 4 },
            minHeight: theme.spacing(29.125),
        },
        {
            component: <MonthlyCreditLimitPieContainer />,
            grid: { xs: 12, md: 4 },
            minHeight: theme.spacing(29.125),
        },
        {
            component: <InstallmentCreditLimitPieContainer />,
            grid: { xs: 12, md: 4 },
            minHeight: theme.spacing(29.125),
        },
    ];

    return (
        <>
            <Box display="flex" flexDirection="row" justifyContent="flex-end">
                <Restricted to="edit.limits">
                    <ChipButton
                        isLoading={limitsLoading}
                        onClick={handleClick}
                        label={getEnumTranslation('edit')}
                        variant="editMenu"
                    />
                </Restricted>
            </Box>
            <Box
                sx={{
                    mb: 2,
                    mt: 2,
                }}>
                <Typography variant="overline">
                    <FormattedMessage
                        id="Limits.pageTitle"
                        defaultMessage="Account & program limits"
                    />
                </Typography>
            </Box>
            <Grid container spacing={2}>
                {panelsToDisplay.map((aPanel, index) => {
                    return <OverviewItem key={index} {...aPanel} />;
                })}
            </Grid>
            <EditLimitsDialog
                open={open}
                setOpen={setOpen}
                limits={limits}
                refetch={refetch}
            />
        </>
    );
};
export default Limits;
